import React, {
    CSSProperties,
    Dispatch,
    Fragment,
    KeyboardEvent as ReactKeyboardEvent,
    MutableRefObject,
    PropsWithChildren,
    Reducer,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState
} from "react";

// @ts-expect-error
import { css, CssObject } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { MotionContainerProps } from "./components/motion-container";
import { useForcedNav } from "./hooks/use-forced-nav";
import { GLOBAL_NAV_HEIGHT } from "./nav-global";
import GlobalNavLink from "./nav-link";

export type DropdownStateProps = {
    readonly currentOpenDropDown: string;
    readonly setCurrentOpenDropDown: React.Dispatch<
        React.SetStateAction<string>
    >;
};

// Dropdown visibility is driven by opacity + visibility transitions rather than
// keyframe animations that toggle display. Animating display: none↔block via
// keyframes is unreliable — the property is not interpolatable and causes
// flickering when multiple state changes happen in quick succession (e.g.
// mousedown closing the dropdown followed immediately by mouseenter re-opening
// it before the animation frame settles).
//
// Instead we keep the panel in the DOM at all times (no display toggling) and
// use:
//   - opacity: 0 + visibility: hidden  → invisible and non-interactive
//   - opacity: 1 + visibility: visible → visible and interactive
//   - transition on both properties    → smooth 125ms fade
//
// visibility: hidden is chosen over display: none because it participates in
// CSS transitions (unlike display) while still removing the element from tab
// order and pointer events.

const TRANSITION_DURATION = "125ms";

const dropdownTransitionBase = css({
    transition: `opacity ${TRANSITION_DURATION} linear, visibility ${TRANSITION_DURATION} linear`
});

const dropdownHidden = css({
    opacity: 0,
    visibility: "hidden",
    pointerEvents: "none"
});

const dropdownVisible = css({
    opacity: 1,
    visibility: "visible",
    pointerEvents: "auto"
});

// Overlay uses the same pattern at a lower opacity target
const overlayHidden = css({
    opacity: 0,
    visibility: "hidden"
});

const overlayVisible = css({
    opacity: 0.7,
    visibility: "visible"
});

const DropdownContentStyle = css({
    opacity: 0,
    visibility: "hidden",
    pointerEvents: "none",
    insetBlockStart: GLOBAL_NAV_HEIGHT,
    minWidth: "264px",
    width: "max-content",
    position: "absolute",
    overflow: "hidden",
    borderRadius: "15px",
    border: "none",
    boxShadow: `0 0 1px 0 ${wacToken(
        "color.neutral.light300"
    )}, 0 5px 20px -5px rgba(0,0,0,0.1)`,
    order: 100,
    backgroundColor: wacToken("color.neutral.light0"),
    transition: `opacity 125ms linear, visibility 125ms linear, width 200ms ease`,
    zIndex: 10
});

const DropdownContentStyleGlobalNav = css({
    insetInline: "0",
    marginInline: "auto",
    transform: "translateX(-24%)" // ensure it fits on screen and still is connected to parent nav link
});

const listContainer = css({
    display: "flex",
    alignItems: "center",
    height: "100%"
});

export const SubnavLayout = {
    Product: 4,
    Teams: 3,
    WhyAtlassian: 4,
    Resources: 4,
    Solutions: 4
} as const;

export type GlobalNavLinkContentProps = {
    readonly globalLinksKey: string;
    readonly linkText: string;
    readonly hasSubnav: boolean;
    readonly isActive?: boolean;
    readonly href?: string;
    readonly productKey?: string;
    readonly content?: JSX.Element | string;
    readonly layout?: SubnavLayout;
    readonly containerPosition?: CSSProperties["position"];
    readonly containerStyles?: CssObject<unknown> | CssObject<unknown>[];
};

function layoutToWidth(layout: SubnavLayout | undefined) {
    switch (layout) {
        case 4:
            return "57rem";
        case 3:
            return "43.5rem";
        default:
            return "auto";
    }
}

type GlobalNavState = {
    current: string | null;
    prev: string | null;
    // True when the currently-open subnav was opened (or confirmed) by a click.
    // While true, hovering other nav links will NOT open their subnavs —
    // only a click can switch the active subnav.
    isClickLocked: boolean;
};

type GlobalNavContextType = GlobalNavState & {
    globalNavItemKeys: Array<string>;
    direction: MotionContainerProps["direction"];
    setCurrent: Dispatch<string | null>;
    setCurrentByClick: Dispatch<string | null>;
    dropdownRefCallback: (node: HTMLDivElement | null) => void;
    dropdownRefCurrentNode: HTMLDivElement | null;
    isLoggedInUserProfileDropdownOpen: boolean;
    setIsLoggedInUserProfileDropdownOpen: Dispatch<SetStateAction<boolean>>;
};

// Two action types: hover-driven (does not set click lock) and
// click-driven (sets click lock so other links ignore hover).
type NavAction =
    | { type: "hover"; key: string | null }
    | { type: "click"; key: string | null };

const GlobalNavContext = createContext<GlobalNavContextType | null>(null);

export const GlobalNavProvider = ({
    globalNavItemKeys,
    children
}: PropsWithChildren<{ globalNavItemKeys: Array<string> }>) => {
    const { forcedNavKey, isForcingNav } = useForcedNav();

    const [dropdownRefCurrentNode, setDropdownRefCurrent] =
        useState<HTMLDivElement | null>(null);

    const [
        isLoggedInUserProfileDropdownOpen,
        setIsLoggedInUserProfileDropdownOpen
    ] = useState(false);

    // using callback ref to set the isGlobalNavDropdownOpen state when dropdownRef is updated
    const dropdownRefCallback = useCallback((node: HTMLDivElement | null) => {
        setDropdownRefCurrent(node);
    }, []);

    const [state, dispatch] = useReducer<Reducer<GlobalNavState, NavAction>>(
        function reducer(state, action) {
            // If we're forcing a nav item, ignore state changes (local dev only)
            if (isForcingNav && forcedNavKey) {
                return {
                    ...state,
                    current: forcedNavKey,
                    prev: state.current,
                    isClickLocked: state.isClickLocked
                };
            }

            const prev = state.current;

            if (action.type === "click") {
                // Click always updates current; lock is set when a subnav is
                // being opened (key is non-empty), cleared when closing.
                return {
                    ...state,
                    current: action.key,
                    prev,
                    isClickLocked: !!action.key
                };
            }

            // hover: only move current if we are NOT click-locked,
            // or if the hover is closing the nav (key is falsy / "exitDiv").
            if (state.isClickLocked && action.key) {
                return state;
            }

            return {
                ...state,
                current: action.key,
                prev,
                isClickLocked: false
            };
        },
        {
            // Initialize with forced nav if present (local development only)
            current: isForcingNav && forcedNavKey ? forcedNavKey : null,
            prev: null,
            isClickLocked: false
        }
    );

    const { current, prev, isClickLocked } = state;

    // Hover-driven setter — will be blocked by click lock on other links
    const setCurrent = useCallback(
        (key: string | null) => dispatch({ type: "hover", key }),
        []
    );

    // Click-driven setter — always wins and sets the click lock
    const setCurrentByClick = useCallback(
        (key: string | null) => dispatch({ type: "click", key }),
        []
    );

    const value = useMemo(() => {
        return {
            current,
            prev,
            isClickLocked,
            globalNavItemKeys,
            direction: "none" as MotionContainerProps["direction"],
            setCurrent,
            setCurrentByClick,
            dropdownRefCallback,
            dropdownRefCurrentNode,
            isLoggedInUserProfileDropdownOpen,
            setIsLoggedInUserProfileDropdownOpen
        };
    }, [
        current,
        prev,
        isClickLocked,
        globalNavItemKeys,
        setCurrent,
        setCurrentByClick,
        dropdownRefCallback,
        dropdownRefCurrentNode,
        isLoggedInUserProfileDropdownOpen,
        setIsLoggedInUserProfileDropdownOpen
    ]);

    return (
        <GlobalNavContext.Provider value={value}>
            {children}
        </GlobalNavContext.Provider>
    );
};

export const useGlobalNavContext = () => {
    const context = useContext(GlobalNavContext);

    if (!context) {
        throw new Error(
            "useGlobalNavContext must be used within GlobalNavProvider"
        );
    }

    return context;
};

export const useExitCurrentNavOnHover = () => {
    const { setCurrent } = useGlobalNavContext();

    const handleMouseLeave = useCallback(() => {
        setCurrent("exitDiv");
    }, [setCurrent]);

    return { handleMouseLeave };
};

export const DivOnHoverExit = ({
    children
}: {
    children: React.ReactElement;
}) => {
    const isHover = !checkFeatureFlagGate(featureFlags.gwpGlobalNavHoverVClick);
    const { handleMouseLeave } = useExitCurrentNavOnHover();
    const navRef = useRef<HTMLDivElement>(null);

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLDivElement>) => {
            // Only close the dropdown if focus is moving outside the nav area
            if (
                navRef.current &&
                e.relatedTarget &&
                !navRef.current.contains(e.relatedTarget as Node)
            ) {
                handleMouseLeave();
            }
        },
        [handleMouseLeave]
    );

    return (
        <div
            ref={navRef}
            css={css({ height: "100%" })}
            onMouseLeave={isHover ? handleMouseLeave : undefined}
            onBlur={isHover ? handleBlur : undefined}
        >
            {children}
        </div>
    );
};

const MoreOrLess = ({
    showButton,
    isShowingMore,
    onClick,
    onExpand
}: {
    showButton: boolean;
    onClick: () => void;
    isShowingMore?: boolean;
    onExpand?: () => void;
}) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const visibility: CSSProperties["visibility"] = showButton
        ? "visible"
        : "hidden";

    const handleClick = () => {
        const wasShowingLess = !isShowingMore;
        onClick();

        // move focus to first revealed item when expanding
        if (wasShowingLess && onExpand) {
            // using useEffect causes a race condition so using setTimeout to ensure state update
            // and DOM changes complete before focusing on the item
            setTimeout(() => {
                onExpand();
            }, 0);
        }

        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "linkButton",
            source: isShowingMore ? "globalNav-less" : "globalNav-more",
            attributes: {
                label: isShowingMore ? "less--" : "more-+"
            }
        }).fire();
    };

    const onKeyDown = (e: ReactKeyboardEvent) => {
        if (e.code === "Enter" || e.code === "Space") {
            handleClick();
        }
    };

    return (
        <li
            aria-hidden={true}
            style={{
                visibility,
                position: showButton ? "initial" : "absolute"
            }}
            data-element-globalnav={isShowingMore ? "less" : "more"}
            data-element-visibility={visibility}
            data-testid="moreOrLess-button"
            css={listContainer}
        >
            <span
                role="button"
                tabIndex={0}
                onClick={handleClick}
                onKeyDown={onKeyDown}
                css={css({
                    paddingBlockStart: token("space.100"),
                    display: "flex",
                    alignItems: "center",
                    lineHeight: "1rem",
                    cursor: "pointer",
                    ["&:hover"]: {
                        color: wacToken("color.extended.blue800")
                    }
                })}
            >
                {isShowingMore ? (
                    <span
                        css={css({ color: wacToken("color.neutral.light700") })}
                    >
                        {intl.formatMessage({
                            id: "globalNav.V2.navlink.lessMinus",
                            defaultMessage: "Less -"
                        })}
                    </span>
                ) : (
                    <span>
                        {intl.formatMessage({
                            id: "globalNav.V2.navlink.morePlus",
                            defaultMessage: "More +"
                        })}
                    </span>
                )}
            </span>
        </li>
    );
};

const GlobalNavLinksContainer = ({
    globalLinksProps,
    globalLinksRef,
    maxLinks = globalLinksProps.length,
    showMore,
    setShowMore,
    isSubnav = false
}: {
    globalLinksProps: GlobalNavLinkContentProps[];
    globalLinksRef?: MutableRefObject<HTMLUListElement | null>;
    maxLinks?: number;
    showMore?: boolean;
    setShowMore?: Dispatch<SetStateAction<boolean>>;
    isSubnav?: boolean;
}) => {
    const {
        current,
        setCurrent,
        setCurrentByClick,
        dropdownRefCallback,
        dropdownRefCurrentNode
    } = useGlobalNavContext();

    // Create refs for navigation links to manage focus
    const navLinkRefs = useRef<(HTMLLIElement | null)[]>([]);

    const handleEscKeyEvent = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                // Use setCurrentByClick to also clear the click lock
                setCurrentByClick("");
            }
        },
        [setCurrentByClick]
    );

    // In click mode (feature gate enabled), onMouseLeave on DivOnHoverExit is
    // disabled, so clicking outside the nav is the only way to dismiss an open
    // dropdown. We attach a mousedown listener for this — but we must exclude
    // clicks on the nav link anchors themselves, since onClick on those already
    // handles the toggle. Without this exclusion, mousedown would close the
    // dropdown before onClick fires, making onClick see isCurrentOpenDropdown
    // as false and re-open it (the original flicker race condition).
    // In hover mode this listener is not attached — DivOnHoverExit.onMouseLeave
    // handles dismissal and there is no click-outside scenario to handle.
    const isHover = !checkFeatureFlagGate(featureFlags.gwpGlobalNavHoverVClick);
    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (
                dropdownRefCurrentNode &&
                !dropdownRefCurrentNode.contains(event.target as Node)
            ) {
                // Also check the target isn't a nav link anchor — onClick on
                // those handles the toggle, and we must not race with it.
                const isNavAnchor = (event.target as Element)?.closest?.(
                    ".glob-nav-anchor-link"
                );
                if (!isNavAnchor) {
                    // Use setCurrentByClick to also clear the click lock
                    setCurrentByClick("");
                }
            }
        },
        [setCurrentByClick, dropdownRefCurrentNode]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleEscKeyEvent, false);
        return () => {
            document.removeEventListener("keydown", handleEscKeyEvent, false);
        };
    }, [handleEscKeyEvent]);

    useEffect(() => {
        if (isHover) return;
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isHover, handleClickOutside]);

    useEffect(() => {
        if (current === "exitDiv") {
            // Use setCurrent (hover-driven) — mouse leaving the nav clears hover state
            // but does not interfere with click-lock (which is already false in hover mode).
            setCurrent("");
        }
    }, [current, setCurrent]);

    const showMoreButton = maxLinks < globalLinksProps.length;

    const showMoreGridTemplateColumns = showMore
        ? globalLinksProps.length + 1
        : maxLinks === globalLinksProps.length
        ? maxLinks
        : maxLinks + 1;

    // focus the first newly revealed navigation item
    const handleFocusFirstHiddenItem = useCallback(() => {
        // focus the first hidden item (at index maxLinks)
        const firstHiddenLink = navLinkRefs.current[maxLinks];
        if (firstHiddenLink) {
            // find the first focusable element (the anchor tag) within the li
            const anchorElement = firstHiddenLink.querySelector("a");
            if (anchorElement) {
                anchorElement.focus();
            }
        }
    }, [maxLinks]);

    return (
        <>
            <button
                type="button"
                css={[
                    css({
                        position: "fixed",
                        width: "100%",
                        height: "100%",
                        insetBlockStart: "0",
                        insetInlineStart: "0",
                        insetInlineEnd: "0",
                        insetBlockEnd: "0",
                        backgroundColor: wacToken("color.neutral.light0"),
                        zIndex: "-1",
                        transition: `opacity ${TRANSITION_DURATION} linear, visibility ${TRANSITION_DURATION} linear`,
                        cursor: "pointer",
                        // Reset button styles — this is a purely visual backdrop
                        padding: "0",
                        margin: "0",
                        border: "none",
                        appearance: "none"
                    }),
                    current &&
                    globalLinksProps.some(
                        (l) => l.globalLinksKey === current && l.hasSubnav
                    )
                        ? overlayVisible
                        : overlayHidden
                ]}
                onClick={() => setCurrentByClick("")}
                aria-label="Close navigation"
                tabIndex={-1}
            />
            <DivOnHoverExit>
                <ul
                    className="glob-nav-links-container-ul"
                    css={[
                        css({
                            display: "grid",
                            gridTemplateColumns: "repeat(5, max-content)",
                            alignItems: "center",
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            height: "100%",
                            position: "relative",
                            [media.above.md]: {
                                marginInlineStart: token("space.300")
                            }
                        })
                    ]}
                    style={{
                        gridTemplateColumns: `repeat(${
                            !isSubnav ? showMoreGridTemplateColumns : maxLinks
                        }, max-content)`
                    }}
                    ref={globalLinksRef}
                >
                    {globalLinksProps.map(
                        // eslint-disable-next-line complexity
                        (globalLink: GlobalNavLinkContentProps, index) => {
                            const containerPosition =
                                globalLink.containerPosition || "initial";
                            const isVisible =
                                globalLink.hasSubnav &&
                                globalLink.globalLinksKey === current;
                            const shouldShowMore =
                                index + 1 <= maxLinks || showMore;

                            const computedLayoutNumber = globalLink?.layout;

                            return (
                                <Fragment key={globalLink.globalLinksKey}>
                                    <li
                                        ref={(el) => {
                                            navLinkRefs.current[index] = el;
                                        }}
                                        style={{
                                            position: shouldShowMore
                                                ? containerPosition
                                                : "absolute",
                                            visibility: shouldShowMore
                                                ? "visible"
                                                : "hidden",
                                            // Ensure absolute items don't take up space in the grid
                                            // and are moved off-screen so we don't see a "flash" of items
                                            // when the hook is re-calculating the nav links size.
                                            // Using a very large negative value ensures they aren't visible
                                            // while they are being measured.
                                            insetBlockStart: shouldShowMore
                                                ? "auto"
                                                : "-9999px",
                                            insetInlineStart: shouldShowMore
                                                ? "auto"
                                                : "-9999px"
                                        }}
                                        css={[
                                            listContainer,
                                            isSubnav &&
                                                css({
                                                    marginBlockEnd:
                                                        token("space.075")
                                                })
                                        ]}
                                    >
                                        <GlobalNavLink
                                            {...globalLink}
                                            isLinkVisible={shouldShowMore}
                                        />
                                        <div
                                            css={[
                                                DropdownContentStyle,
                                                !isSubnav &&
                                                    DropdownContentStyleGlobalNav,
                                                dropdownTransitionBase,
                                                isVisible
                                                    ? dropdownVisible
                                                    : dropdownHidden
                                            ]}
                                            ref={
                                                isVisible
                                                    ? dropdownRefCallback
                                                    : null
                                            }
                                            aria-hidden={!globalLink?.content}
                                            style={{
                                                width: layoutToWidth(
                                                    computedLayoutNumber
                                                ),
                                                ...globalLink?.containerStyles
                                            }}
                                        >
                                            {globalLink.content}
                                        </div>
                                    </li>
                                </Fragment>
                            );
                        }
                    )}
                    {!isSubnav && !!setShowMore && (
                        <MoreOrLess
                            showButton={showMoreButton}
                            isShowingMore={showMore}
                            onClick={() => setShowMore((prev) => !prev)}
                            onExpand={handleFocusFirstHiddenItem}
                        />
                    )}
                </ul>
            </DivOnHoverExit>
        </>
    );
};

export const DropdownContentWrapper = ({ children }: PropsWithChildren) => (
    <div>{children}</div>
);

export default GlobalNavLinksContainer;

export type SubnavLayout = (typeof SubnavLayout)[keyof typeof SubnavLayout];
